import { Controller } from "@hotwired/stimulus"

// Skeleton + fallback for lazy-loaded recipe images.
//
// On a fresh mount, shows a shimmer until the <img> fires `load` (real
// image visible) or `error` (placeholder icon visible).
//
// Idempotent across Turbo navigations: previously, a card whose image
// 404'd or errored once would mutate the DOM (image hidden, fallback
// shown). When the user navigated away and Turbo restored the cached
// snapshot — common on iOS Hotwire Native swiping back to dashboard /
// index tile views — the mutation persisted. So a subsequent re-render
// with a FRESH signed URL (Active Storage URLs rotate hourly) would
// still show the placeholder icon stuck on top of a perfectly good
// image. `connect()` now resets the DOM to its initial state on every
// mount, then re-evaluates whether the current image is loadable.
export default class extends Controller {
  static targets = ["image", "loader", "fallback"]

  connect() {
    this.#resetToInitialState()
    if (!this.hasImageTarget) return
    const img = this.imageTarget
    if (!img.complete) return // wait for load/error event

    if (img.naturalHeight > 0) {
      this.loaded()
    } else {
      this.error()
    }
  }

  loaded() {
    if (this.hasLoaderTarget) this.loaderTarget.remove()
    // Re-show the image and re-hide the fallback in case a prior error()
    // call mutated them — see the connect() docstring above.
    if (this.hasImageTarget) this.imageTarget.style.display = ""
    if (this.hasFallbackTarget) this.fallbackTarget.classList.add("hidden")
  }

  error() {
    if (this.hasLoaderTarget) this.loaderTarget.remove()
    if (this.hasImageTarget) this.imageTarget.style.display = "none"
    if (this.hasFallbackTarget) this.fallbackTarget.classList.remove("hidden")
  }

  #resetToInitialState() {
    if (this.hasImageTarget) this.imageTarget.style.display = ""
    if (this.hasFallbackTarget) this.fallbackTarget.classList.add("hidden")
  }
}
